import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ContactAvatarPicker,
  avatarUploadErrorMessage,
  type ContactAvatarImage,
} from '@/components/common/ContactAvatar';
import api from '@/services/api';
import { useAuthStore } from '@/store/auth';

/**
 * The `/customers/me/` payload and the small pieces that render it.
 *
 * These moved here out of `MyContactsPage` because the blocks they draw moved:
 * C1 puts My Profile and KYC Status on the customer's Settings page, P1 puts
 * the agent's own details on the POSP's. `MyContactsPage` still imports the
 * type, the hook and `ContactRow` for the "My Broker" card it keeps.
 *
 * `/customers/me/` answers BOTH portal logins. A customer gets their Customer
 * row; a POSP has no Customer row, so the endpoint returns their broker's
 * contacts plus their own name/phone/email instead (apps/customers/views.py).
 * `customer_code` is therefore the discriminator: present only for a customer.
 */
export interface CustomerMe {
  // Broker / POSP contacts (returned for both customer and POSP logins)
  broker_name?: string;
  broker_phone?: string;
  broker_email?: string;
  contact_person_name?: string;
  posp_name?: string;
  posp_phone?: string;
  posp_email?: string;
  // Customer profile (only present for a customer login)
  customer_code?: string;
  salutation?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  date_of_birth?: string;
  gender?: string;
  email?: string;
  phone?: string;
  alternate_phone?: string;
  whatsapp_number?: string;
  address_line1?: string;
  address_line2?: string;
  /**
   * B2: `city` is an FK id (a UUID) -- `Customer.city` points into the seeded
   * `geography.City` table. `city_name` is the readable half, and it is the one
   * that goes on screen; `city` is here only because the edit form on
   * ProfilePage posts a value back into it (a name or an id, both accepted by
   * `CustomerProfileSerializer.validate_city`).
   */
  city?: string;
  city_name?: string | null;
  state?: string;
  pincode?: string;
  kyc_status?: string;
  kyc_verified_at?: string;
  customer_since?: string;
  // F2: the contact's own profile picture. `id` seeds the placeholder colour so
  // the customer sees the same circle their broker and POSP see -- it is the
  // same Customer row on every screen.
  id?: string;
  avatar?: string | null;
  avatar_image?: ContactAvatarImage | null;
}

export function useCustomerMe() {
  return useQuery<CustomerMe>({
    queryKey: ['customer-me'],
    queryFn: async () => {
      const res = await api.get('/customers/me/');
      return res.data;
    },
    // There are TWO cache keys over `/customers/me/` in this app: this one and
    // `['customer','me']` (hooks/useCustomers), which is what the editable
    // /settings/profile screen reads and what `useUpdateCustomerMe`
    // invalidates. So an edit made there does NOT invalidate this copy, and
    // with the app-wide 5-minute staleTime the summary card here would keep
    // showing the pre-edit values after the user navigated back. Refetching on
    // mount costs one small GET on a page the user opened deliberately, and it
    // is the fix that does not require reaching into another lane's hook.
    refetchOnMount: 'always',
  });
}

export function ContactRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <span className="mt-0.5 text-gray-400 flex-shrink-0">{icon}</span>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
}

// CUSTOMER-2B: map the raw backend kyc_status onto a human journey stage.
export const KYC_STAGE: Record<
  string,
  { label: string; variant: 'default' | 'warning' | 'success' | 'error' }
> = {
  pending: { label: 'Not started', variant: 'default' },
  submitted: { label: 'Pending broker verification', variant: 'warning' },
  under_review: { label: 'Pending broker verification', variant: 'warning' },
  verified: { label: 'Verified', variant: 'success' },
  completed: { label: 'Verified', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'error' },
  expired: { label: 'Expired', variant: 'error' },
  reverify_required: { label: 'Re-verification required', variant: 'warning' },
};

/**
 * F2: the contact's own profile picture, set by the contact themselves.
 *
 * Posts to `POST /customers/me/avatar/`, the action built for exactly this
 * caller: a customer has no media-library access, so they never name an asset
 * id — the endpoint resolves their own row from the token and files the new
 * asset under their broker's library. No id crosses the wire, so this cannot
 * touch anyone else's contact. The upload lands on the same Customer row the
 * broker admin, broker employee and POSP read, which is why it shows up on
 * their screens with no extra plumbing.
 */
export function ProfilePhotoSection({ profile, name }: { profile?: CustomerMe; name: string }) {
  const queryClient = useQueryClient();
  const { setUser } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    // This page's own query plus the shared `useCustomerMe` hook's key --
    // both cache a copy of avatar_image.
    queryClient.invalidateQueries({ queryKey: ['customer-me'] });
    queryClient.invalidateQueries({ queryKey: ['customer', 'me'] });
  };

  // C1 close-out: `me_avatar` now writes `User.profile_picture` alongside
  // `Customer.avatar` and echoes the new value back as `profile_picture` --
  // mirror it into the auth store here so the header avatar (which reads
  // `useAuthStore().user.profile_picture`, never this page's own query)
  // updates immediately too. Same merge shape as
  // `useUpdateProfileSettings` (hooks/useSettings.ts): spread the current
  // user and override only the field that changed.
  const syncHeaderAvatar = (pictureUrl: string) => {
    const current = useAuthStore.getState().user;
    if (current) {
      setUser({ ...current, profile_picture: pictureUrl });
    }
  };

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/customers/me/avatar/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Optimisation and rendition generation run in-request server-side
        // (no Celery here), so the 30s client default is too tight for a
        // full-resolution phone photo on a slow uplink.
        timeout: 180000,
      });
      return data;
    },
    onSuccess: (data) => {
      setError(null);
      refresh();
      if (typeof data?.profile_picture === 'string') {
        syncHeaderAvatar(data.profile_picture);
      }
      toast.success('Profile picture updated');
    },
    onError: (err) => setError(avatarUploadErrorMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.delete('/customers/me/avatar/');
      return data;
    },
    onSuccess: (data) => {
      setError(null);
      refresh();
      if (typeof data?.profile_picture === 'string') {
        syncHeaderAvatar(data.profile_picture);
      }
      toast.success('Profile picture removed');
    },
    onError: (err) => setError(avatarUploadErrorMessage(err)),
  });

  return (
    <div className="mb-4 border-b border-gray-100 pb-4 dark:border-gray-700">
      <ContactAvatarPicker
        contactId={profile?.id}
        name={name}
        image={profile?.avatar_image}
        onSelect={(file) => {
          setError(null);
          uploadMutation.mutate(file);
        }}
        onRemove={() => removeMutation.mutate()}
        isBusy={uploadMutation.isPending || removeMutation.isPending}
        error={error}
        hint="Your broker and your policy agent see this picture too."
      />
    </div>
  );
}

/** `[salutation] first middle last`, the way both portal screens print it. */
export function customerDisplayName(data?: CustomerMe): string {
  return [data?.salutation, data?.first_name, data?.middle_name, data?.last_name]
    .filter(Boolean)
    .join(' ');
}

export function customerAddress(data?: CustomerMe): string {
  // `city_name`, never `city` -- see the interface above. Printing the FK put a
  // raw UUID in the middle of the customer's own address.
  return [data?.address_line1, data?.address_line2, data?.city_name, data?.state, data?.pincode]
    .filter(Boolean)
    .join(', ');
}
